import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useArenaStore } from "../store/useArenaStore";
import { useGameStore } from "../store/useGameStore";
import { formatPartyArenaScheduleLocal } from "../utils/localSchedule";
import { CLASS_LABEL } from "../utils/assets";
import type { PartyMember } from "../api/arena";

type Tab = "solo" | "party" | "shop";

const SOLO_MAX_RANK = 30;

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatWindowCountdown(isoEnd: string | null): string | null {
  if (!isoEnd) return null;
  const end = new Date(isoEnd).getTime();
  if (Number.isNaN(end)) return null;
  const sec = Math.max(0, Math.floor((end - Date.now()) / 1000));
  return formatCountdown(sec);
}

export function ArenaScreen() {
  const { arenaTab, setArenaTab } = useArenaStore();
  const [tab, setTab] = useState<Tab>(arenaTab);
  const [cooldownTick, setCooldownTick] = useState(0);
  const { setScreen, balance, user } = useGameStore();
  const {
    soloRanks,
    myRank,
    soloRules,
    dailyAttempts,
    lastAttack,
    skipCooldownDiamonds,
    vipSkipFree,
    soloCooldownExempt,
    attemptCooldownSec,
    shopItems,
    leagueTier,
    leaguePoints,
    fetchSoloRanks,
    attackSolo,
    skipCooldown,
    fetchShop,
    buyItem,
    fetchLeagueStatus,
    fetchSchedule,
    scheduleOpen,
    partyWindows,
    scheduleWindowEnd,
    scheduleNextStart,
    adminOverride,
    myPartyRoom,
    partyRooms,
    partyMatchmaking,
    partyRematchPending,
    partyExpiredModal,
    partyRewardAttemptsUsed,
    partyRewardAttemptsLimit,
    partyRewardsExhausted,
    refreshPartyRooms,
    pollParty,
    createPartyRoom,
    joinPartyRoom,
    leavePartyRoom,
    togglePartyReady,
    cancelPartyCountdown,
    dismissPartyExpiredModal,
    leaveCurrentPartyRoom,
    loading,
    error,
  } = useArenaStore();

  const [windowTick, setWindowTick] = useState(0);
  const [roomTick, setRoomTick] = useState(0);

  useEffect(() => {
    setTab(arenaTab);
  }, [arenaTab]);

  useEffect(() => {
    if (tab === "solo") {
      void fetchSoloRanks();
      void fetchLeagueStatus();
    } else if (tab === "shop") {
      void fetchShop();
    } else if (tab === "party") {
      void fetchSchedule();
      void pollParty();
      void refreshPartyRooms();
    }
  }, [tab]);

  const partyPollMs = useMemo(() => {
    if (!myPartyRoom) return 3000;
    if (myPartyRoom.status === "countdown") return 1000;
    if (myPartyRoom.status === "waiting") {
      const team = myPartyRoom.members.filter((m): m is PartyMember => m != null);
      if (team.length > 0 && team.every((m) => m.ready)) return 1000;
    }
    return 3000;
  }, [myPartyRoom]);

  useEffect(() => {
    if (tab !== "party") return;
    void pollParty();
    const pollTimer = setInterval(() => {
      void pollParty();
    }, partyPollMs);
    const roomsTimer = setInterval(() => {
      void refreshPartyRooms();
    }, 5000);
    return () => {
      clearInterval(pollTimer);
      clearInterval(roomsTimer);
    };
  }, [tab, pollParty, refreshPartyRooms, partyPollMs]);

  useEffect(() => {
    if (tab !== "party" || !scheduleWindowEnd) return;
    const t = setInterval(() => setWindowTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [tab, scheduleWindowEnd]);

  useEffect(() => {
    if (!myPartyRoom) return;
    const t = setInterval(() => setRoomTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [myPartyRoom]);

  void windowTick;
  void roomTick;

  const partyScheduleLabel = useMemo(
    () =>
      partyWindows.length > 0
        ? formatPartyArenaScheduleLocal(partyWindows)
        : "Loading schedule...",
    [partyWindows],
  );

  useEffect(() => {
    if (!lastAttack) return;
    const timer = setInterval(() => setCooldownTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, [lastAttack]);

  const isUnranked = myRank == null || myRank > SOLO_MAX_RANK;
  const cooldownRemaining =
    soloCooldownExempt || !lastAttack
      ? 0
      : Math.max(
          0,
          attemptCooldownSec -
            Math.floor((Date.now() - new Date(lastAttack).getTime()) / 1000),
        );
  void cooldownTick;

  const rulesHint = soloRules
    ? `±${soloRules.rank_attack_above} ranks · beat higher ranks to take their spot · lower ranks: rewards only`
    : "Beat higher ranks to climb; lower ranks give rewards without swapping places.";

  const fightDisabledGlobal = loading || dailyAttempts >= 10 || cooldownRemaining > 0;

  const formatCooldown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setScreen("main")}
          className="px-3 py-1 bg-kingdom-mid rounded text-gray-300 text-sm"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold text-gold-400">Arena</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {(["solo", "party", "shop"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setTab(t);
              setArenaTab(t);
            }}
            className={clsx(
              "flex-1 py-2 rounded-xl font-bold transition-colors",
              tab === t
                ? "bg-amber-700 text-white"
                : "bg-kingdom-mid text-gray-400"
            )}
          >
            {t === "solo" && "Solo Arena"}
            {t === "party" && "Party Arena"}
            {t === "shop" && "Arena Shop"}
          </button>
        ))}
      </div>

      {/* Solo Arena */}
      {tab === "solo" && (
        <div className="space-y-3">
          {error && (
            <div className="p-3 bg-rose-900/30 rounded-xl border border-rose-700/50">
              <p className="text-sm text-rose-300">{error}</p>
            </div>
          )}

          <div className="p-3 bg-kingdom-mid rounded-xl">
            <p className="text-sm text-gray-300">
              Your Rank:{" "}
              <span className="text-amber-400 font-bold">
                {isUnranked ? "Unranked" : myRank}
              </span>
            </p>
            <p className="text-sm text-gray-300">
              Attempts: <span className="text-amber-400">{dailyAttempts}/10</span>
            </p>
            {cooldownRemaining > 0 && !soloCooldownExempt && (
              <div className="mt-2 space-y-2">
                <p className="text-sm text-rose-300">
                  Cooldown: {formatCooldown(cooldownRemaining)}
                </p>
                <button
                  type="button"
                  onClick={() => skipCooldown()}
                  disabled={
                    loading ||
                    (!vipSkipFree && (balance?.diamonds ?? 0) < skipCooldownDiamonds)
                  }
                  className="w-full py-2 rounded-xl border border-kingdom-glow text-kingdom-glow font-bold text-sm disabled:opacity-40"
                >
                  {vipSkipFree
                    ? "Skip free (VIP)"
                    : `Skip for 💎${skipCooldownDiamonds}`}
                </button>
                {!vipSkipFree && skipCooldownDiamonds > 0 && (
                  <p className="text-[10px] text-gray-500 text-center">Free with VIP at higher tiers</p>
                )}
              </div>
            )}
            <p className="text-sm text-gray-300">
              League: <span className="text-purple-400">{leagueTier}</span> ({leaguePoints} pts)
            </p>
            <p className="text-xs text-gray-500 mt-2">{rulesHint}</p>
          </div>

          {dailyAttempts >= 10 && (
            <div className="p-3 bg-rose-900/30 rounded-xl border border-rose-700/50">
              <p className="text-sm text-rose-300">Daily attempts exhausted. Reset at midnight UTC.</p>
            </div>
          )}

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {soloRanks.map((entry) => (
              <div
                key={entry.rank}
                className={clsx(
                  "p-3 rounded-xl border",
                  entry.user_id === user?.id
                    ? "bg-amber-900/30 border-amber-700/50"
                    : "bg-kingdom-mid border-kingdom-light"
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-amber-100">
                      #{entry.rank} {entry.name} {entry.is_npc && "(NPC)"}
                    </p>
                    <p className="text-xs text-gray-400">
                      {entry.class} Lv.{entry.level} • BR {entry.br.toLocaleString()}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => attackSolo(entry.rank, "rotation")}
                    disabled={fightDisabledGlobal || !entry.can_fight}
                    title={
                      !entry.can_fight && entry.fight_block_reason === "self"
                        ? "This is you"
                        : !entry.can_fight && entry.fight_block_reason === "out_of_range"
                          ? "Out of attack range"
                          : undefined
                    }
                    className="px-4 py-2 bg-rose-700 text-white rounded-xl font-bold disabled:opacity-40 shrink-0"
                  >
                    Fight
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Party Arena */}
      {tab === "party" && (
        <PartyArenaTab
          scheduleOpen={scheduleOpen}
          partyScheduleLabel={partyScheduleLabel}
          scheduleWindowEnd={scheduleWindowEnd}
          scheduleNextStart={scheduleNextStart}
          adminOverride={adminOverride}
          myPartyRoom={myPartyRoom}
          partyRooms={partyRooms}
          partyMatchmaking={partyMatchmaking}
          partyRematchPending={partyRematchPending}
          partyExpiredModal={partyExpiredModal}
          partyRewardAttemptsUsed={partyRewardAttemptsUsed}
          partyRewardAttemptsLimit={partyRewardAttemptsLimit}
          partyRewardsExhausted={partyRewardsExhausted}
          roomTick={roomTick}
          loading={loading}
          error={error}
          userId={user?.id}
          onCreateRoom={() => void createPartyRoom()}
          onJoinRoom={(id) => void joinPartyRoom(id)}
          onLeaveRoom={() => void leavePartyRoom()}
          onToggleReady={() => void togglePartyReady()}
          onCancelCountdown={() => void cancelPartyCountdown()}
          onDismissExpired={() => dismissPartyExpiredModal()}
          onResetParty={() => void leaveCurrentPartyRoom()}
        />
      )}

      {/* Arena Shop */}
      {tab === "shop" && (
        <div className="space-y-3">
          <div className="p-3 bg-kingdom-mid rounded-xl">
            <p className="text-sm text-gray-300">
              Insignia: <span className="text-amber-400 font-bold">{balance?.insignia ?? 0}</span>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {shopItems.map((item) => (
              <div key={item.item_id} className="p-3 bg-kingdom-mid rounded-xl">
                <p className="font-bold text-amber-100 text-sm">{item.name}</p>
                <p className="text-xs text-gray-400 mt-1">{item.insignia_cost} Insignia</p>
                <button
                  type="button"
                  onClick={() => buyItem(item.item_id, 1)}
                  disabled={loading || (balance?.insignia ?? 0) < item.insignia_cost}
                  className="w-full mt-2 py-1 bg-amber-700 text-white rounded text-sm font-bold disabled:opacity-40"
                >
                  Buy
                </button>
              </div>
            ))}
          </div>
        </div>
      )}


      {error && tab === "shop" && (
        <div className="p-3 bg-rose-900/30 rounded-xl border border-rose-700/50">
          <p className="text-sm text-rose-300">{error}</p>
        </div>
      )}
    </div>
  );
}

function PartyArenaTab({
  scheduleOpen,
  partyScheduleLabel,
  scheduleWindowEnd,
  scheduleNextStart,
  adminOverride,
  myPartyRoom,
  partyRooms,
  partyMatchmaking,
  partyRematchPending,
  partyExpiredModal,
  partyRewardAttemptsUsed,
  partyRewardAttemptsLimit,
  partyRewardsExhausted,
  roomTick,
  loading,
  error,
  userId,
  onCreateRoom,
  onJoinRoom,
  onLeaveRoom,
  onToggleReady,
  onCancelCountdown,
  onDismissExpired,
  onResetParty,
}: {
  scheduleOpen: boolean;
  partyScheduleLabel: string;
  scheduleWindowEnd: string | null;
  scheduleNextStart: string | null;
  adminOverride: boolean;
  myPartyRoom: import("../api/arena").PartyRoomDetail | null;
  partyRooms: import("../api/arena").PartyRoomDetail[];
  partyMatchmaking: boolean;
  partyRematchPending: boolean;
  partyExpiredModal: string | null;
  partyRewardAttemptsUsed: number;
  partyRewardAttemptsLimit: number;
  partyRewardsExhausted: boolean;
  roomTick: number;
  loading: boolean;
  error: string | null;
  userId?: number;
  onCreateRoom: () => void;
  onJoinRoom: (roomId: string) => void;
  onLeaveRoom: () => void;
  onToggleReady: () => void;
  onCancelCountdown: () => void;
  onDismissExpired: () => void;
  onResetParty: () => void;
}) {
  void roomTick;
  const windowCountdown = formatWindowCountdown(scheduleWindowEnd);
  const nextStartLabel = scheduleNextStart
    ? new Date(scheduleNextStart).toLocaleString(undefined, {
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  if (partyExpiredModal) {
    return (
      <div className="space-y-3">
        <div className="p-4 bg-kingdom-mid rounded-xl text-center space-y-3">
          <p className="text-rose-300">{partyExpiredModal}</p>
          <button
            type="button"
            onClick={onDismissExpired}
            className="px-4 py-2 bg-kingdom-glow text-kingdom-dark rounded-xl font-bold"
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  if (myPartyRoom?.status === "countdown") {
    const isLeader = myPartyRoom.leader_id === userId;
    const fightStartsAtMs =
      myPartyRoom.fight_starts_at != null ? myPartyRoom.fight_starts_at * 1000 : null;
    const fightIn =
      fightStartsAtMs != null
        ? Math.max(0, Math.ceil((fightStartsAtMs - Date.now()) / 1000))
        : (myPartyRoom.countdown_seconds_remaining ?? 0);
    return (
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-rose-900/30 rounded-xl border border-rose-700/50">
            <p className="text-sm text-rose-300">{error}</p>
          </div>
        )}
        <div className="p-6 bg-rose-950/40 border border-rose-600/50 rounded-xl text-center space-y-3">
          <p className="text-lg font-bold text-rose-200">Fight starts in</p>
          <p className="text-5xl font-black text-gold-400 tabular-nums">{Math.max(0, fightIn)}</p>
          <p className="text-xs text-gray-400">Matchmaking begins when the timer hits zero</p>
        </div>
        {isLeader && (
          <button
            type="button"
            onClick={onCancelCountdown}
            disabled={loading}
            className="w-full py-3 rounded-xl font-bold border border-gray-600 text-gray-200 disabled:opacity-40"
          >
            Cancel
          </button>
        )}
      </div>
    );
  }

  if (
    myPartyRoom &&
    (myPartyRoom.status === "waiting" ||
      myPartyRoom.status === "started" ||
      partyMatchmaking ||
      partyRematchPending)
  ) {
    const me = myPartyRoom.members.find((m) => m && userId && m.user_id === userId);
    const isLeader = myPartyRoom.leader_id === userId;
    const team = myPartyRoom.members.filter((m): m is PartyMember => m != null);
    const allReady = team.length > 0 && team.every((m) => m.ready);
    const countdown = myPartyRoom.seconds_remaining;
    const rematchCooldown = myPartyRoom.rematch_seconds_remaining ?? 0;

    return (
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-rose-900/30 rounded-xl border border-rose-700/50">
            <p className="text-sm text-rose-300">{error}</p>
          </div>
        )}

        <div className="text-center space-y-1">
          <div className="text-4xl">⚔️</div>
          <h2 className="text-lg font-bold text-gold-400">Party Arena Lobby</h2>
          <p className="text-xs text-gray-400">1–3 players · team vs team or NPC fallback</p>
          <p className="text-xs text-gray-500">
            Reward battles today: {partyRewardAttemptsUsed}/{partyRewardAttemptsLimit}
          </p>
        </div>

        {partyRewardsExhausted && (
          <div className="p-3 bg-amber-900/30 border border-amber-600/50 rounded-xl text-center">
            <p className="text-xs text-amber-200">
              Daily reward limit — fights still count, no gold/insignia/league until midnight UTC.
            </p>
          </div>
        )}

        {partyRematchPending && !isLeader && myPartyRoom.status === "started" && (
          <div className="p-3 bg-violet-900/30 border border-violet-600/40 rounded-xl text-center">
            <p className="text-sm text-violet-200">Waiting for party leader to finish review and start rematch</p>
          </div>
        )}

        {allReady && myPartyRoom.status === "waiting" && !partyMatchmaking && (
          <div className="p-3 bg-emerald-900/25 border border-emerald-600/40 rounded-xl text-center">
            <p className="text-sm text-emerald-200">
              {myPartyRoom.auto_start_paused_after_cancel
                ? "Countdown cancelled — toggle Ready to confirm lineup"
                : rematchCooldown > 0
                  ? `Next fight in ${rematchCooldown}s`
                  : "Starting matchmaking…"}
            </p>
          </div>
        )}

        {partyMatchmaking ? (
          <div className="p-4 bg-amber-900/30 border border-amber-600/50 rounded-xl text-center">
            <p className="text-amber-200 font-semibold">Matching opponent…</p>
            <p className="text-xs text-gray-400 mt-1">Up to 10 seconds, then NPC squad</p>
          </div>
        ) : (
          <div className="bg-kingdom-purple border border-kingdom-glow rounded-xl p-4 text-center">
            <div className="text-xs text-gray-400 uppercase">Room closes in</div>
            <div className="text-3xl font-black text-gold-400 tabular-nums">{formatCountdown(countdown)}</div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          {myPartyRoom.members.map((m, i) => (
            <div
              key={i}
              className={clsx(
                "rounded-xl border p-3 min-h-[5rem] flex flex-col justify-center",
                m
                  ? "border-kingdom-mid bg-kingdom-dark"
                  : "border-dashed border-gray-700 bg-kingdom-dark/30",
              )}
            >
              {m ? (
                <>
                  <p className="font-bold text-sm text-amber-100 truncate">{m.name}</p>
                  <p className="text-xs text-gray-400">
                    {CLASS_LABEL[m.class] ?? m.class} · BR {m.br.toLocaleString()}
                  </p>
                  <p className={clsx("text-xs mt-1 font-semibold", m.ready ? "text-emerald-400" : "text-gray-500")}>
                    {m.ready ? "Ready" : "Not ready"}
                  </p>
                </>
              ) : (
                <p className="text-xs text-gray-600 text-center">Empty</p>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          {!partyMatchmaking && (
            <button
              type="button"
              onClick={onToggleReady}
              disabled={loading || !me}
              className={clsx(
                "w-full py-3 rounded-xl font-bold border",
                me?.ready
                  ? "border-gray-600 text-gray-300"
                  : "border-emerald-600 text-emerald-300 bg-emerald-900/20",
              )}
            >
              {me?.ready ? "Unready" : "Ready"}
            </button>
          )}
          <button
            type="button"
            onClick={onLeaveRoom}
            disabled={loading || partyMatchmaking}
            className="w-full py-2 rounded-xl text-sm text-gray-400 border border-gray-700 disabled:opacity-40"
          >
            {isLeader ? "Disband room" : "Leave room"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        className={clsx(
          "p-3 rounded-xl border text-sm",
          scheduleOpen
            ? "bg-emerald-900/20 border-emerald-600/40 text-emerald-200"
            : "bg-kingdom-mid border-kingdom-light text-gray-300",
        )}
      >
        <p className="font-semibold">{scheduleOpen ? "Open now" : "Closed"}</p>
        <p className="text-xs mt-1 text-gray-400">{partyScheduleLabel}</p>
        {adminOverride && (
          <p className="text-xs mt-1 text-violet-300">Admin override active</p>
        )}
        {scheduleOpen && windowCountdown && (
          <p className="text-xs mt-1">Closes in {windowCountdown}</p>
        )}
        {!scheduleOpen && nextStartLabel && (
          <p className="text-xs mt-1">Next window: {nextStartLabel}</p>
        )}
      </div>

      <p className="text-xs text-center text-gray-400">
        Reward battles today: {partyRewardAttemptsUsed}/{partyRewardAttemptsLimit}
      </p>
      {partyRewardsExhausted && (
        <div className="p-3 bg-amber-900/30 border border-amber-600/50 rounded-xl text-center">
          <p className="text-sm text-amber-200">
            Daily reward limit reached — you can still fight, but without gold, insignia, or league
            points until midnight UTC.
          </p>
        </div>
      )}

      {error && (
        <div className="p-3 bg-rose-900/30 rounded-xl border border-rose-700/50">
          <p className="text-sm text-rose-300">{error}</p>
        </div>
      )}

      {scheduleOpen ? (
        <>
          <button
            type="button"
            onClick={onCreateRoom}
            disabled={loading || !!myPartyRoom}
            className="w-full py-3 rounded-xl font-bold bg-amber-700 text-white disabled:opacity-40"
          >
            Create Room
          </button>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-300">Open rooms</p>
            {partyRooms.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-4">No rooms yet — create one!</p>
            ) : (
              partyRooms.map((room) => {
                const leader = room.members.find((m) => m && m.user_id === room.leader_id);
                const team = room.members.filter((m): m is PartyMember => m != null);
                const displayName = leader?.name ?? team[0]?.name ?? "Party";
                return (
                  <div
                    key={room.room_id}
                    className="p-3 bg-kingdom-mid rounded-xl border border-kingdom-light flex items-center justify-between gap-2"
                  >
                    <div>
                      <p className="font-bold text-amber-100 text-sm">
                        {displayName}&apos;s party
                      </p>
                      <p className="text-xs text-gray-400">
                        {room.team_size}/3 · BR {room.team_br.toLocaleString()} ·{" "}
                        {formatCountdown(room.seconds_remaining)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onJoinRoom(room.room_id)}
                      disabled={loading || room.team_size >= 3}
                      className="px-3 py-1.5 bg-kingdom-glow text-kingdom-dark rounded-lg text-sm font-bold disabled:opacity-40"
                    >
                      Join
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        <div className="p-4 bg-kingdom-mid rounded-xl text-center text-gray-400 text-sm space-y-2">
          <p>Party Arena opens on schedule. Admins can force-open for testing.</p>
          <button
            type="button"
            onClick={onResetParty}
            className="text-xs text-gray-500 underline hover:text-gray-300"
          >
            Reset stuck party room
          </button>
        </div>
      )}

      {scheduleOpen && !myPartyRoom && (
        <button
          type="button"
          onClick={onResetParty}
          className="w-full text-xs text-gray-500 underline hover:text-gray-300 py-1"
        >
          Reset stuck party room
        </button>
      )}
    </div>
  );
}
